import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Camera, FileText, Sparkles, AlertTriangle, Loader2, Shield, CheckCircle, XCircle, ChevronDown, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import api from '../lib/api';
import { toast } from 'sonner';
import { useSafePostHog } from '../components/PostHogProvider';

const IngredientChecker = () => {
  const posthog = useSafePostHog();
  const [catProfiles, setCatProfiles] = useState([]);
  const [selectedCat, setSelectedCat] = useState('none');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [foodName, setFoodName] = useState('');
  const [brand, setBrand] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(null);
  const [showFeedbackOptions, setShowFeedbackOptions] = useState(false);
  const [catInsights, setCatInsights] = useState([]);
  const navigate = useNavigate();
  const [catRecommendations, setCatRecommendations] = useState([]);

  const fetchInsights = async (catId) => {
    if (!catId || catId === 'none') return;
    try {
      const res = await api.get(`/cat-insights/${catId}`);
      if (res.data?.insights?.length > 0) setCatInsights(res.data.insights);
      if (res.data?.recommendations?.length > 0) setCatRecommendations(res.data.recommendations);
      else setCatRecommendations([]);
    } catch { /* silent */ }
  };

  const handleSetIntent = async () => {
    if (!result?.id || feedbackSent) return;
    try {
      await api.post('/set-intent', { scan_id: result.id, cat_id: selectedCat });
      setFeedbackSent('intent_set');
      toast.success("Awesome! We'll check back with you in a couple days 😸");
    } catch {
      toast.error('Could not save intent');
    }
  };

  useEffect(() => { loadCatProfiles(); }, []);

  useEffect(() => {
    if (!loading) {
      setLoadingTextIndex(0);
      return;
    }
    const selectedCatObj = catProfiles.find(c => c.id === selectedCat);
    const catName = selectedCatObj ? selectedCatObj.name : "your cat";
    const texts = [
      'Reading label...',
      'Analyzing nutritional value...',
      `Generating ${catName}'s report...`
    ];
    
    const interval = setInterval(() => {
      setLoadingTextIndex((prev) => (prev + 1) % texts.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [loading, selectedCat, catProfiles]);

  const loadCatProfiles = async () => {
    try {
      const r = await api.get('/cat-profiles');
      setCatProfiles(r.data);
      if (r.data.length > 0) setSelectedCat(r.data[0].id);
      else setSelectedCat('none');
    } catch { setSelectedCat('none'); }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) { setImageFile(file); const reader = new FileReader(); reader.onloadend = () => setImagePreview(reader.result); reader.readAsDataURL(file); }
  };

  const compressImage = (file, maxWidth = 1024, quality = 0.75) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality).split(',')[1]);
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  const pollTaskStatus = async (taskId) => {
    try {
      const interval = setInterval(async () => {
        try {
          const r = await api.get(`/analysis/status/${taskId}`);
          if (r.data.status === 'completed') {
            clearInterval(interval);
            setResult(r.data.result);
            toast.success('Analysis complete!');
            setLoading(false);
            if (selectedCat && selectedCat !== 'none') fetchInsights(selectedCat);
          } else if (r.data.status === 'error') {
            clearInterval(interval);
            setResult({ error: 'IMAGE_UNCLEAR', message: r.data.error });
            toast.error(r.data.error || 'Analysis failed.');
            setLoading(false);
          }
        } catch (err) {
          clearInterval(interval);
          toast.error('Lost connection to analysis server.');
          setLoading(false);
        }
      }, 3000);
    } catch (err) {
      setLoading(false);
    }
  };

  const executeScan = async (payload, isRetry = false) => {
    try {
      const r = await api.post('/ingredient-scan', payload);
      if (r.data.task_id) {
        pollTaskStatus(r.data.task_id);
      } else if (r.data.error) {
        setResult({ error: 'IMAGE_UNCLEAR', message: r.data.message || r.data.error });
        toast.warning(r.data.message || 'Analysis failed.');
        setLoading(false);
      }
    } catch (error) {
      if (!isRetry && (error.response?.status === 503 || error.response?.status === 504)) {
        toast.info('High volume. Retrying in 5 seconds...', { duration: 4000 });
        setTimeout(() => executeScan(payload, true), 5000);
      } else {
        toast.error(error.response?.status === 403 ? error.response.data.detail : 'Analysis failed to start.');
        setLoading(false);
      }
    }
  };

  const handleTextScan = async () => {
    if (!textInput.trim()) { toast.error('Please enter ingredients'); return; }
    posthog.capture('scan_started', { scan_type: 'ingredients_paste' });
    setLoading(true);
    setFeedbackSent(null);
    setShowFeedbackOptions(false);
    executeScan({ cat_id: selectedCat !== 'none' ? selectedCat : null, food_name: foodName, brand, ingredients_raw: textInput });
  };

  const handleImageScan = async () => {
    if (!imageFile) { toast.error('Please select an image'); return; }
    posthog.capture('scan_started', { scan_type: 'image_upload' });
    setLoading(true);
    setFeedbackSent(null);
    setShowFeedbackOptions(false);
    try {
      const base64Compressed = await compressImage(imageFile);
      executeScan({ cat_id: selectedCat !== 'none' ? selectedCat : null, food_name: foodName, brand, image_base64: base64Compressed });
    } catch (e) {
      toast.error('Failed to pre-process image.');
      setLoading(false);
    }
  };

  const getStatusColor = (color) => ({ GREEN: 'bg-[#BFDB38] text-[#1B4332]', YELLOW: 'bg-yellow-400 text-[#1B4332]', RED: 'bg-[#FF6B6B] text-white' })[color?.toUpperCase()] || 'bg-gray-400 text-white';
  const getStatusBorder = (color) => ({ GREEN: 'border-[#BFDB38]', YELLOW: 'border-yellow-400', RED: 'border-[#FF6B6B]' })[color?.toUpperCase()] || 'border-gray-200';
  const getActionBadge = (action) => ({ SAFE: 'bg-[#BFDB38] text-[#1B4332]', CAUTION: 'bg-yellow-400 text-[#1B4332]', WARNING: 'bg-[#FF6B6B] text-white' })[action?.toUpperCase()] || 'bg-gray-400 text-white';
  const getActionBorder = (action) => ({ SAFE: 'border-[#BFDB38] bg-[#BFDB38]/5', CAUTION: 'border-yellow-400 bg-yellow-50', WARNING: 'border-[#FF6B6B] bg-[#FF6B6B]/5' })[action?.toUpperCase()] || 'border-gray-200 bg-gray-50';

  const currentCatObj = catProfiles.find(c => c.id === selectedCat);
  const loadingTexts = [
    'Reading label...',
    'Analyzing nutritional value...',
    `Generating ${currentCatObj ? currentCatObj.name + "'s" : "your cat's"} report...`
  ];

  return (
    <DashboardLayout title="INGREDIENT CHECKER">
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-8">
        <div className="bg-white border-2 border-[#1B4332]/8 rounded-2xl p-4 sm:p-6" data-testid="scanner-section">
          <h2 className="text-xs uppercase tracking-[0.15em] font-bold text-[#FF6B6B] mb-5">SCAN FOOD</h2>
          <div className="space-y-4 mb-6">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#1B4332]/60">Cat Profile</Label>
              <Select value={selectedCat} onValueChange={setSelectedCat}>
                <SelectTrigger className="border-2 border-[#1B4332]/10 rounded-xl" data-testid="cat-select"><SelectValue placeholder="Select a cat" /></SelectTrigger>
                <SelectContent>
                  {catProfiles.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  <SelectItem value="none">No specific cat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#1B4332]/60">Food Name</Label>
                <input type="text" value={foodName} onChange={(e) => setFoodName(e.target.value)} className="mt-1 w-full px-4 py-2 border-2 border-[#1B4332]/10 rounded-xl focus:border-[#FF6B6B] focus:ring-4 focus:ring-[#FF6B6B]/10 transition-all text-sm" placeholder="e.g., Chicken & Rice (AI will auto-detect if empty)" data-testid="food-name-input" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#1B4332]/60">Brand</Label>
                <input type="text" value={brand} onChange={(e) => setBrand(e.target.value)} className="mt-1 w-full px-4 py-2 border-2 border-[#1B4332]/10 rounded-xl focus:border-[#FF6B6B] focus:ring-4 focus:ring-[#FF6B6B]/10 transition-all text-sm" placeholder="e.g., Blue Buffalo (AI will auto-detect if empty)" data-testid="brand-input" />
              </div>
            </div>
            <p className="text-[11px] text-gray-400 italic flex items-center justify-center gap-1.5 pt-1 pb-2">
              <Sparkles className="w-3.5 h-3.5 text-[#BFDB38]" /> Leave blank to let our AI identify the product for you.
            </p>
          </div>

          <Tabs defaultValue="text" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4 bg-[#1B4332]/5 rounded-full p-1">
              <TabsTrigger value="text" className="rounded-full data-[state=active]:bg-[#1B4332] data-[state=active]:text-white font-bold text-sm" data-testid="text-tab">
                <FileText className="w-4 h-4 mr-2" strokeWidth={2.5} /> Text
              </TabsTrigger>
              <TabsTrigger value="image" className="rounded-full data-[state=active]:bg-[#1B4332] data-[state=active]:text-white font-bold text-sm" data-testid="image-tab">
                <Camera className="w-4 h-4 mr-2" strokeWidth={2.5} /> Image
              </TabsTrigger>
            </TabsList>
            <TabsContent value="text" className="space-y-4">
              <Textarea value={textInput} onChange={(e) => setTextInput(e.target.value)} className="border-2 border-[#1B4332]/10 rounded-xl focus:border-[#FF6B6B] min-h-[120px] sm:min-h-[180px] text-sm" placeholder="Paste the ingredients list here..." data-testid="ingredients-textarea" />
              <Button onClick={handleTextScan} disabled={loading} className="w-full rounded-full font-bold bg-[#1B4332] hover:bg-[#2D6A4F] text-white transition-all duration-200" data-testid="scan-text-btn">
                {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" strokeWidth={2.5} /> Analyzing...</> : <><Sparkles className="w-5 h-5 mr-2" strokeWidth={2.5} /> Analyze Ingredients</>}
              </Button>
            </TabsContent>
            <TabsContent value="image" className="space-y-4">
              <div className="border-2 border-dashed border-[#1B4332]/15 rounded-2xl p-8 text-center hover:border-[#FF6B6B] transition-colors">
                {imagePreview ? (
                  <div className="space-y-4">
                    <img src={imagePreview} alt="Preview" className="max-h-52 mx-auto rounded-xl" />
                    <Button variant="outline" onClick={() => { setImageFile(null); setImagePreview(''); }} className="rounded-full border-2">Remove</Button>
                  </div>
                ) : (
                  <label className="cursor-pointer block">
                    <Camera className="w-14 h-14 text-[#1B4332]/15 mx-auto mb-3" strokeWidth={2.5} />
                    <p className="text-[#1B4332] font-bold text-sm mb-1">Upload Food Label Photo</p>
                    <p className="text-xs text-[#1B4332]/50 mb-4">Take a clear photo of the ingredients list</p>
                    <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" data-testid="image-upload-input" />
                    <span className="inline-block px-6 py-2 bg-[#1B4332] text-white rounded-full font-bold text-sm">Choose Image</span>
                  </label>
                )}
              </div>
              <Button onClick={handleImageScan} disabled={loading || !imageFile} className="w-full rounded-full font-bold bg-[#1B4332] hover:bg-[#2D6A4F] text-white transition-all duration-200" data-testid="scan-image-btn">
                {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" strokeWidth={2.5} /> Analyzing...</> : <><Sparkles className="w-5 h-5 mr-2" strokeWidth={2.5} /> Analyze Image</>}
              </Button>
            </TabsContent>
          </Tabs>
        </div>

        <div className="bg-white border-2 border-[#1B4332]/8 rounded-2xl p-4 sm:p-6" data-testid="results-section">
          <h2 className="text-xs uppercase tracking-[0.15em] font-bold text-[#FF6B6B] mb-5">ANALYSIS RESULTS</h2>
          {loading ? (
            <div className="space-y-6" data-testid="loading-skeleton">
              <div className="flex flex-col items-center justify-center p-6 border-2 border-[#1B4332]/5 rounded-2xl min-h-48 bg-[#1B4332]/5">
                <Loader2 className="w-10 h-10 text-[#BFDB38] animate-spin mb-4" strokeWidth={2.5} />
                <div className="h-8 flex items-center justify-center overflow-hidden">
                  <p key={loadingTextIndex} className="text-[#1B4332] font-semibold text-[15px] text-center animate-in slide-in-from-bottom-2 fade-in duration-300">
                    {loadingTexts[loadingTextIndex]}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-center p-6 border-2 border-[#1B4332]/5 rounded-2xl animate-pulse">
                <div className="h-3 w-28 bg-[#1B4332]/10 mb-5 rounded-full"></div>
                <div className="w-24 h-24 bg-[#1B4332]/10 rounded-2xl mb-5"></div>
                <div className="h-5 w-48 bg-[#1B4332]/10 rounded-full mb-3"></div>
                <div className="h-4 w-64 bg-[#1B4332]/5 rounded-full"></div>
              </div>
              <div className="p-5 border-2 border-[#1B4332]/5 rounded-xl flex items-start gap-4 animate-pulse">
                <div className="w-8 h-8 bg-[#1B4332]/10 rounded-full shrink-0"></div>
                <div className="space-y-3 w-full mt-1">
                  <div className="h-3 w-1/4 bg-[#1B4332]/10 rounded-full"></div>
                  <div className="h-4 w-full bg-[#1B4332]/5 rounded-full"></div>
                  <div className="h-4 w-5/6 bg-[#1B4332]/5 rounded-full"></div>
                </div>
              </div>
            </div>
          ) : !result ? (
            <div className="text-center py-16 animate-in fade-in duration-500">
              <Sparkles className="w-14 h-14 text-[#1B4332]/10 mx-auto mb-3" strokeWidth={2.5} />
              <p className="text-[#1B4332]/50 font-bold">Scan ingredients to see analysis</p>
            </div>
          ) : result.error === 'SERVICE_BUSY' ? (
            <div className="space-y-4 animate-in fade-in duration-500" data-testid="service-busy-warning">
              <div className="text-center p-6 bg-red-50 border border-red-200 rounded-xl">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <AlertTriangle className="w-7 h-7 text-red-500" strokeWidth={2} />
                </div>
                <h3 className="text-base font-extrabold text-[#1B4332] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>Service Busy</h3>
                <p className="text-[13px] text-[#1B4332]/60 max-w-xs mx-auto">
                  {result.message || "Servers are currently under high demand. Please retry in 10 seconds."}
                </p>
              </div>
            </div>
          ) : result.error === 'IMAGE_UNCLEAR' ? (
            <div className="space-y-4 animate-in fade-in duration-500" data-testid="image-unclear-warning">
              <div className="text-center p-6 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <AlertTriangle className="w-7 h-7 text-amber-500" strokeWidth={2} />
                </div>
                <h3 className="text-base font-extrabold text-[#1B4332] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>Image Not Clear Enough</h3>
                <p className="text-[13px] text-[#1B4332]/60 max-w-xs mx-auto">
                  {result.message || "Oops! The photo is a bit blurry. Please take a clearer picture of the ingredient list for an accurate grade."}
                </p>
              </div>
              <div className="p-4 bg-[#1B4332]/5 border border-[#1B4332]/8 rounded-xl">
                <p className="text-[11px] uppercase tracking-widest font-semibold text-[#1B4332]/40 mb-2">Tips for better results</p>
                <ul className="space-y-1.5 text-[13px] text-[#1B4332]/60">
                  <li className="flex items-start gap-2"><span className="text-amber-500 mt-0.5">•</span> Hold the camera steady and close to the label</li>
                  <li className="flex items-start gap-2"><span className="text-amber-500 mt-0.5">•</span> Ensure good lighting with no shadows</li>
                  <li className="flex items-start gap-2"><span className="text-amber-500 mt-0.5">•</span> Or paste the ingredients text manually instead</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="space-y-5 animate-in fade-in zoom-in-95 duration-500" data-testid="analysis-results">
              {/* ═══ DECISION ENGINE: Hero Decision Card ═══ */}
              {result.combined_message ? (
                <>
                  <div className={`text-center p-6 rounded-2xl border-2 ${
                    result.quality === 'good' ? 'border-emerald-300 bg-gradient-to-b from-emerald-50 to-emerald-100/30' :
                    result.quality === 'average' ? 'border-amber-300 bg-gradient-to-b from-amber-50 to-amber-100/30' :
                    'border-red-300 bg-gradient-to-b from-red-50 to-red-100/30'
                  }`} data-testid="hero-decision-card">
                    {result.cat_name && (
                      <div className="inline-flex items-center justify-center bg-[#1B4332] text-white px-3.5 py-1.5 rounded-full text-[10px] font-bold tracking-widest mb-4 shadow-sm">
                        FOR {result.cat_name.toUpperCase()}
                      </div>
                    )}

                    {/* Combined Message Text */}
                    {result.combined_message && result.combined_message.includes('\n') ? (
                      <div className="mb-5">
                        <h3 className={`text-2xl sm:text-3xl font-black mb-1.5 ${
                          result.quality === 'good' ? 'text-emerald-800' :
                          result.quality === 'average' ? 'text-amber-800' : 'text-red-800'
                        }`} style={{ fontFamily: 'Nunito, sans-serif' }}>
                          {result.combined_message.split('\n')[0]}
                        </h3>
                        <p className={`text-[13px] font-bold ${
                          result.quality === 'good' ? 'text-emerald-800/60' :
                          result.quality === 'average' ? 'text-amber-800/60' : 'text-red-800/60'
                        }`}>
                          {result.combined_message.split('\n')[1]}
                        </p>
                      </div>
                    ) : (
                      <h3 className={`text-2xl sm:text-3xl font-black mb-5 ${
                        result.quality === 'good' ? 'text-emerald-800' :
                        result.quality === 'average' ? 'text-amber-800' : 'text-red-800'
                      }`} style={{ fontFamily: 'Nunito, sans-serif' }}>
                        {result.combined_message}
                      </h3>
                    )}

                    {/* Side-by-side Dual Metrics */}
                    <div className="flex flex-wrap justify-center gap-3 mb-2">
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-[#1B4332]/10 shadow-sm">
                        <span className="text-[11px] uppercase tracking-widest font-bold text-[#1B4332]/50">Acceptance:</span>
                        <span className={`text-[13px] font-black ${
                          result.acceptance === 'high' ? 'text-emerald-700' :
                          result.acceptance === 'medium' ? 'text-amber-700' : 'text-red-700'
                        }`}>
                          {result.acceptance === 'high' ? 'High' : result.acceptance === 'medium' ? 'Medium' : 'Low'}
                        </span>
                      </div>
                      
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-[#1B4332]/10 shadow-sm">
                        <span className="text-[11px] uppercase tracking-widest font-bold text-[#1B4332]/50">Quality:</span>
                        <span className={`text-[13px] font-black ${
                          result.quality === 'good' ? 'text-emerald-700' :
                          result.quality === 'average' ? 'text-amber-700' : 'text-red-700'
                        }`}>
                          {result.quality === 'good' ? 'Good' : result.quality === 'average' ? 'Average' : 'Poor'}
                        </span>
                      </div>
                    </div>

                    {result.food_name && <p className="text-sm font-semibold text-[#1B4332]/60 mt-4">{result.food_name} {result.brand ? `by ${result.brand}` : ''}</p>}
                  </div>

                  {/* ═══ PREDICTION Section ═══ */}
                  {result.prediction && (
                    <div className={`p-4 rounded-xl border-2 ${
                      result.prediction === 'high' ? 'border-emerald-200 bg-emerald-50/50' :
                      result.prediction === 'moderate' || result.prediction === 'medium' ? 'border-amber-200 bg-amber-50/50' :
                      'border-red-200 bg-red-50/50'
                    }`}>
                      <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#1B4332]/50 mb-1">LIKELIHOOD OF ACCEPTANCE</p>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-lg font-black ${
                          result.prediction === 'high' ? 'text-emerald-700' :
                          result.prediction === 'moderate' || result.prediction === 'medium' ? 'text-amber-700' :
                          'text-red-700'
                        }`}>
                          {result.prediction === 'high' ? 'High Chance' :
                           result.prediction === 'moderate' || result.prediction === 'medium' ? 'Moderate Chance' : 'Low Chance'}
                        </span>
                        <span className="text-xl">
                          {result.prediction === 'high' ? '😸' :
                           result.prediction === 'moderate' || result.prediction === 'medium' ? '🤔' : '😿'}
                        </span>
                      </div>
                      {result.prediction_reason && (
                        <p className="text-[13px] text-[#1B4332]/70 font-medium leading-relaxed">{result.prediction_reason}</p>
                      )}
                      {result.reasoning && (
                        <p className="text-[12px] text-[#1B4332]/60 font-medium leading-relaxed italic mt-1.5">{result.reasoning}</p>
                      )}
                    </div>
                  )}

                  {/* ═══ NOTE Section ═══ */}
                  {result.note && (
                    <div className="p-4 bg-[#1B4332]/5 border border-[#1B4332]/10 rounded-xl flex items-start gap-3">
                      <Sparkles className="w-4 h-4 text-[#BFDB38] shrink-0 mt-0.5" />
                      <p className="text-[13px] text-[#1B4332]/70 font-medium leading-relaxed">{result.note}</p>
                    </div>
                  )}

                  {/* ═══ WHY Section — Food Quality ═══ */}
                  {result.reasons && result.reasons.length > 0 && (
                    <div className="bg-white border-2 border-[#1B4332]/8 rounded-2xl p-5">
                      <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#FF6B6B] mb-3">WHY (Food Quality)</p>
                      <ul className="space-y-2.5">
                        {result.reasons.map((reason, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <span className={`mt-1 w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[10px] font-black ${
                              result.quality === 'good' ? 'bg-emerald-100 text-emerald-700' :
                              result.quality === 'average' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                            }`}>{i + 1}</span>
                            <span className="text-sm font-semibold text-[#1B4332]/80">{reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* ═══ REJECTION Section — Behavior ═══ */}
                  {result.possible_rejection_reasons && result.possible_rejection_reasons.length > 0 && (
                    <div className="bg-white border-2 border-[#1B4332]/8 rounded-2xl p-5">
                      <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-indigo-500 mb-3">Possible reason your cat may reject this</p>
                      <ul className="space-y-2.5">
                        {result.possible_rejection_reasons.map((reason, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <span className="text-indigo-400 mt-0.5 w-5 h-5 flex items-center justify-center shrink-0 font-black">•</span>
                            <span className="text-sm font-semibold text-[#1B4332]/80">{reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* ═══ NEXT STEP Section ═══ */}
                  {result.next_step && (
                    <div className={`p-4 rounded-xl border-2 ${
                      result.quality === 'good' ? 'border-emerald-200 bg-emerald-50/50' :
                      result.quality === 'average' ? 'border-amber-200 bg-amber-50/50' :
                      'border-red-200 bg-red-50/50'
                    }`}>
                      <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#1B4332]/50 mb-2">WHAT TO DO NEXT</p>
                      <p className={`text-sm font-bold ${
                        result.quality === 'good' ? 'text-emerald-800' :
                        result.quality === 'average' ? 'text-amber-800' : 'text-red-800'
                      }`}>{result.next_step}</p>
                    </div>
                  )}

                  {/* ═══ 1-TAP FEEDBACK (INTENT) ═══ */}
                  <div className="bg-[#1B4332]/5 border border-[#1B4332]/10 rounded-xl p-4" data-testid="feedback-section">
                    <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#1B4332]/50 mb-3 text-center">TRIED THIS FOOD?</p>
                    {feedbackSent ? (
                      <p className="text-center text-sm font-semibold text-[#1B4332]/60">
                        ✅ Logged — we'll check back with you in a couple of days!
                      </p>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <button onClick={handleSetIntent} className="px-6 py-2.5 bg-[#BFDB38] rounded-xl text-[13px] font-bold text-[#1B4332] shadow-sm hover:bg-[#a6bf30] transition-all">Yes, I'll try this</button>
                        <p className="text-[11px] text-[#1B4332]/40 font-semibold italic text-center">We'll check back in 48 hours for {selectedCat !== 'none' ? catProfiles.find(c => c.id === selectedCat)?.name || 'your cat' : 'your cat'}'s reaction</p>
                      </div>
                    )}
                  </div>

                  {/* ═══ CAT INSIGHTS — What we've learned ═══ */}
                  {catInsights.length > 0 && (
                    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-2xl p-5 animate-in fade-in duration-500" data-testid="cat-insights">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg">🧠</span>
                        <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-purple-700">What we've learned about your cat</p>
                      </div>
                      <ul className="space-y-2">
                        {catInsights.map((insight, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <span className="text-base mt-0.5 shrink-0">{insight.icon}</span>
                            <span className={`text-sm font-semibold ${
                              insight.type === 'sensitivity' ? 'text-red-700' :
                              insight.type === 'dislike' ? 'text-amber-700' : 'text-emerald-700'
                            }`}>{insight.text}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="text-[10px] text-purple-500 font-medium mt-3 italic">These patterns improve your recommendations over time</p>
                    </div>
                  )}

                  {/* ═══ RECOMMENDATIONS — What to try next ═══ */}
                  {catRecommendations.length > 0 && (
                    <div className="bg-gradient-to-br from-[#1B4332]/5 to-[#BFDB38]/10 border-2 border-[#1B4332]/15 rounded-2xl p-5 animate-in fade-in duration-500" data-testid="cat-recommendations">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg">🧭</span>
                        <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#1B4332]/70">What to try next</p>
                      </div>
                      <ul className="space-y-2.5">
                        {catRecommendations.map((rec, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <span className="text-base mt-0.5 shrink-0">{rec.icon}</span>
                            <span className={`text-sm font-semibold ${
                              rec.type === 'avoid' ? 'text-red-700' :
                              rec.type === 'try' ? 'text-emerald-700' :
                              rec.type === 'explore' ? 'text-blue-700' : 'text-amber-700'
                            }`}>{rec.text}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Medical Diet Banner */}
                  {result.is_medical_product && (
                    <div className="bg-[#FFFAEC] border-l-4 border-amber-400 p-4 rounded-r-xl shadow-sm">
                      <p className="font-bold text-amber-900 text-[13px] mb-1">⚠️ Veterinary Diet Detected</p>
                      <p className="text-[11px] text-amber-800/80 leading-relaxed font-semibold">
                        This formula is designed for specific medical therapy. Always follow your Vet's advice.
                      </p>
                    </div>
                  )}

                  {/* ═══ Show Full Analysis Toggle ═══ */}
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="w-full flex items-center justify-center gap-2 py-3 text-sm font-bold text-[#1B4332]/50 hover:text-[#1B4332] transition-colors"
                    data-testid="toggle-details-btn"
                  >
                    <Shield className="w-4 h-4" strokeWidth={2.5} />
                    {showDetails ? 'Hide full ingredient analysis' : 'Show full ingredient analysis'}
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showDetails ? 'rotate-180' : ''}`} />
                  </button>

                  {showDetails && (
                    <div className="space-y-5 animate-in fade-in duration-300">
                      {/* PurrCheck Score Card */}
                      <div className={`text-center p-5 border-2 rounded-2xl ${
                        result.purrcheck_score >= 70 ? 'border-emerald-200 bg-emerald-50/30' :
                        result.purrcheck_score >= 50 ? 'border-amber-200 bg-amber-50/30' :
                        'border-red-200 bg-red-50/30'
                      }`}>
                        <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#1B4332]/60 mb-3">PURRCHECK SCORE</p>
                        {result.purrcheck_score != null && (
                          <div className="flex flex-col items-center">
                            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl font-black ${
                              result.purrcheck_score >= 70 ? 'bg-emerald-100 text-emerald-700' :
                              result.purrcheck_score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'
                            }`}>
                              <span className="flex flex-col items-center leading-none">
                                <span className="text-3xl">{result.purrcheck_score}</span>
                                <span className="text-[10px] font-bold opacity-60">/100</span>
                              </span>
                            </div>
                            <div className={`mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-black border ${
                              result.purrcheck_score >= 70 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              result.purrcheck_score >= 50 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                              'bg-red-50 text-red-700 border-red-200'
                            }`}>Grade {result.purrcheck_grade}</div>
                          </div>
                        )}
                      </div>

                      {/* Ingredient Breakdown Badges */}
                      {(result.harmful_found?.length > 0 || result.caution_found?.length > 0 || result.good_found?.length > 0) && (
                        <div className="bg-white border-2 border-[#1B4332]/8 rounded-2xl p-5 space-y-4">
                          <div className="flex items-center gap-2 mb-1">
                            <Shield className="w-4 h-4 text-[#1B4332]" strokeWidth={2.5} />
                            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#1B4332]/60">Ingredient Breakdown</p>
                          </div>
                          {result.harmful_found?.length > 0 && (
                            <div>
                              <p className="text-[10px] uppercase tracking-widest font-bold text-red-500 mb-2">Harmful Detected</p>
                              <div className="flex flex-wrap gap-1.5">
                                {result.harmful_found.map((item, i) => (
                                  <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-50 border border-red-200 rounded-lg text-[11px] font-bold text-red-700">
                                    <XCircle className="w-3 h-3 shrink-0" /> {item}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {result.caution_found?.length > 0 && (
                            <div>
                              <p className="text-[10px] uppercase tracking-widest font-bold text-amber-600 mb-2">Caution</p>
                              <div className="flex flex-wrap gap-1.5">
                                {result.caution_found.map((item, i) => (
                                  <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-[11px] font-bold text-amber-700">
                                    <AlertTriangle className="w-3 h-3 shrink-0" /> {item}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {result.good_found?.length > 0 && (
                            <div>
                              <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-600 mb-2">Good Ingredients</p>
                              <div className="flex flex-wrap gap-1.5">
                                {result.good_found.map((item, i) => (
                                  <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-[11px] font-bold text-emerald-700">
                                    <CheckCircle className="w-3 h-3 shrink-0" /> {item}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          <p className="text-[10px] text-[#1B4332]/40 italic pt-1">PurrCheck Score is our proprietary rule-based ingredient safety rating</p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                /* ═══ LEGACY FALLBACK for old scan history without decision field ═══ */
                <div className={`text-center p-6 border-2 rounded-2xl ${
                  result.purrcheck_score >= 70 ? 'border-emerald-200 bg-emerald-50/30' :
                  result.purrcheck_score >= 50 ? 'border-amber-200 bg-amber-50/30' :
                  'border-red-200 bg-red-50/30'
                }`}>
                  {result.purrcheck_score != null && (
                    <div className="flex flex-col items-center">
                      <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#1B4332]/60 mb-4">PRODUCT SCORE</p>
                      <div className={`inline-flex items-center justify-center w-24 h-24 rounded-2xl font-black ${
                        result.purrcheck_score >= 70 ? 'bg-emerald-100 text-emerald-700' :
                        result.purrcheck_score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'
                      }`}>
                        <span className="flex flex-col items-center leading-none">
                          <span className="text-4xl">{result.purrcheck_score}</span>
                          <span className="text-xs font-bold opacity-60">/100</span>
                        </span>
                      </div>
                      <div className={`mt-3 inline-flex items-center px-4 py-1.5 rounded-full text-sm font-black border ${
                        result.purrcheck_score >= 70 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        result.purrcheck_score >= 50 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        'bg-red-50 text-red-700 border-red-200'
                      }`}>Grade {result.purrcheck_grade}</div>
                    </div>
                  )}
                  {result.food_name && <p className="text-sm font-semibold text-[#1B4332]/60 mt-3">{result.food_name} {result.brand ? `by ${result.brand}` : ''}</p>}
                </div>
              )}

              {/* Universal Medical Disclaimer */}
              <div className="text-center mt-4 pt-4 border-t border-gray-100">
                <p className="text-[10px] text-gray-500 font-medium leading-relaxed max-w-sm mx-auto">
                  PurrCheck helps you make informed choices. If your cat has a medical condition, consult your Vet before changing diets.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default IngredientChecker;
